import React from "react";
import { SearchHistoryItem } from "../../search-core/history";

export interface SearchHistoryBlockProps {
  /** Array of search history items to display */
  history: SearchHistoryItem[];
  /** Current search query (for highlighting matches) */
  currentQuery?: string;
  /** Callback when a history item is clicked */
  onSelect: (query: string) => void;
  /** Callback when a history item remove button is clicked */
  onRemove?: (query: string) => void;
  /** Optional CSS class name */
  className?: string;
}

/**
 * SearchHistoryBlock - Displays user's search history with quick re-search.
 *
 * Behavior:
 * - Shows list of historical search queries
 * - Filters history by currentQuery (should be done upstream)
 * - Clicking an item triggers onSelect with the query
 * - Shows clock icon for each item
 * - Hidden when history array is empty
 *
 * Design: Similar to CategoriesBlock with simple list styling.
 */
const SearchHistoryBlock: React.FC<SearchHistoryBlockProps> = ({
  history,
  currentQuery: _currentQuery = "",
  onSelect,
  onRemove,
  className = "",
}) => {
  // Don't render if no history
  if (!history.length) {
    return null;
  }

  const handleItemClick = (query: string) => {
    onSelect(query);
  };

  const handleRemove = (query: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (onRemove) {
      onRemove(query);
    }
  };

  return (
    <div className={`search-history-block ${className}`} style={styles.container}>
      <div style={styles.list}>
        {history
          .filter((_, i) => i < 5)
          .map((item) => {
            return (
              <button
                key={`${item.query}-${item.timestamp}`}
                onClick={() => handleItemClick(item.query)}
                style={styles.itemButton}
                aria-label={`Search for "${item.query}" again`}
              >
                <div style={styles.itemContent}>
                  <img src="/src/img/refresh-ccw-01.svg" />
                  <div style={styles.textContainer}>
                    <span className="query-text">{item.query}</span>
                    {/* <span style={styles.timeText}>
                    {formatTime(item.timestamp)}
                  </span> */}
                  </div>
                </div>
                {onRemove && (
                  <button
                    type="button"
                    onClick={(e) => handleRemove(item.query, e)}
                    aria-label={`Remove ${item.query} from history`}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src="/src/img/x.svg"
                      alt="Remove"
                      style={{ width: "16px", height: "16px", opacity: 0.6 }}
                    />
                  </button>
                )}
              </button>
            );
          })}
      </div>

      {/* {history.length > 0 && (
        <div style={styles.footer}>
          <button
            onClick={() => onSelect("")}
            style={styles.clearButton}
            aria-label="Clear search history"
          >
            <i className="fas fa-trash-alt" style={styles.clearIcon}></i>
            Очистить
          </button>
        </div>
      )} */}
    </div>
  );
};

// Minimal inline styles matching CategoriesBlock styling
const styles = {
  container: {},
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: "12px",
    paddingBottom: "8px",
    borderBottom: "1px solid #f1f5f9",
  },
  headerIcon: {
    fontSize: "14px",
    color: "#64748b",
    marginRight: "8px",
  },
  title: {
    margin: 0,
    fontSize: "16px",
    fontWeight: 600,
    color: "",
    flexGrow: 1,
  },
  count: {
    fontSize: "12px",
    color: "#94a3b8",
  },
  list: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "4px",
  },
  itemButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    textAlign: "left" as const,
    transition: "background-color 0.2s ease",
    width: "100%",
  },
  itemContent: {
    display: "flex",
    alignItems: "center",
    flexGrow: 1,
    gap: "8px",
  },
  itemIcon: {
    fontSize: "12px",
    color: "#94a3b8",
    marginRight: "8px",
    flexShrink: 0,
  },
  textContainer: {
    display: "flex",
    flexDirection: "column" as const,
    flexGrow: 1,
  },
  queryText: {
    fontSize: "13px",
    color: "#475569",
    fontWeight: 500,
    marginBottom: "2px",
  },
  highlightedText: {
    color: "#2563eb",
    fontWeight: 600,
  },
  timeText: {
    fontSize: "11px",
    color: "#94a3b8",
  },
  chevronIcon: {
    fontSize: "10px",
    color: "#cbd5e1",
    flexShrink: 0,
  },
  footer: {
    marginTop: "12px",
    paddingTop: "8px",
    borderTop: "1px solid #f1f5f9",
    display: "flex",
    justifyContent: "center",
  },
  clearButton: {
    padding: "4px 8px",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "4px",
    fontSize: "11px",
    color: "#94a3b8",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    transition: "color 0.2s ease",
  },
  clearIcon: {
    fontSize: "10px",
  },
};

export default SearchHistoryBlock;
